import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { useLocation, useParams } from 'react-router-dom';
import * as Api from '../../../../extViewmodels';
import { EmailMailerType } from '../../../../models';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { useCampaignReportContentQuery, useEmailQuery } from '../../../../queries';
import { useAdminAccountQuery } from '../../../../queries/Admin/Account/useAdminAccountQuery';
import { useTemplate } from '../../../../queries/Templates/useTemplate';
import { useTemplatesByType } from '../../../../queries/Templates/useTemplatesByType';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { styleSheet } from './styles';

// Constants
const UNSUBSCRIBE_VISIBILITY_THRESHOLD = 4;
const PLACEHOLDER_TEMPLATE_ID = 'placeholder-template';

export function CampaignDebugView() {
	const { campaignId } = useParams<{ campaignId: string }>();
	const userSession = useUserSession();

	// Check for accountId in query parameters for impersonation context
	const location = useLocation();
	const searchParams = new URLSearchParams(location.search);
	const accountId = searchParams.get('accountId');

	// Create impersonation context - either from query param or existing userSession context
	const impersonationContext = React.useMemo(() => {
		// First priority: accountId from query parameter (from debug button)
		if (accountId) {
			return {
				account: {
					id: accountId,
				},
			} as Api.IImpersonationContext;
		}

		// Second priority: existing impersonation context from userSession
		if (userSession?.impersonationContext) {
			return userSession.impersonationContext;
		}

		return undefined;
	}, [accountId, userSession?.impersonationContext]);

	const campaignQuery = useEmailQuery({ id: campaignId, impersonationContext });
	const campaignReportContentQuery = useCampaignReportContentQuery({
		campaignId,
		impersonationContext,
		enabled: !!campaignId,
	});
	const campaign = campaignQuery.data;

	// Load signature template using React Query
	const forUserId = !impersonationContext?.account?.id ? campaign?.creator?.id : undefined;
	const hasValidSignatureTemplateId = campaign?.signatureTemplateId && campaign.signatureTemplateId !== 'default';

	// Load specific signature template if we have a valid ID (not "default")
	const signatureTemplateQuery = useTemplate({
		templateId: campaign?.signatureTemplateId || PLACEHOLDER_TEMPLATE_ID,
		enabled: hasValidSignatureTemplateId,
		impersonationContext,
		forUserId,
	});

	// Load signature templates by type to find default when no specific ID
	const signatureTemplatesQuery = useTemplatesByType({
		type: Api.TemplateType.Signature,
		enabled: !!campaign && !hasValidSignatureTemplateId,
		impersonationContext,
		forUserId,
		userId: campaign?.creator?.id,
	});

	// Get the signature template (specific or default) with simplified logic
	const signatureTemplate = React.useMemo(() => {
		if (hasValidSignatureTemplateId && signatureTemplateQuery.data) {
			return signatureTemplateQuery.data;
		}

		if (!hasValidSignatureTemplateId && signatureTemplatesQuery.data) {
			const templates = signatureTemplatesQuery.data?.pages?.[0]?.values || [];
			return templates.find((template: Api.ITemplate) => template.defaultTemplate) || templates[0] || null;
		}

		return null;
	}, [hasValidSignatureTemplateId, signatureTemplateQuery.data, signatureTemplatesQuery.data]);

	// Simplified loading and error states
	const signatureTemplateLoading = hasValidSignatureTemplateId
		? signatureTemplateQuery.isLoading
		: signatureTemplatesQuery.isLoading;

	const signatureTemplateError = hasValidSignatureTemplateId
		? signatureTemplateQuery.error
		: signatureTemplatesQuery.error;

	// Fetch account details for email provider configuration
	// Use impersonation context account ID when available, fallback to userSession account ID
	const targetAccountId = impersonationContext?.account?.id || userSession.account.id;
	const accountQuery = useAdminAccountQuery({
		accountId: targetAccountId,
		enabled: !!targetAccountId,
	});

	const [unsubscribeTemplate, setUnsubscribeTemplate] = React.useState<Api.ITemplate | null>(null);
	const [unsubscribeTemplateLoaded, setUnsubscribeTemplateLoaded] = React.useState(false);

	// Load unsubscribe template (keeping existing logic for now)
	React.useEffect(() => {
		const loadUnsubscribeTemplate = async () => {
			try {
				// Load unsubscribe template with impersonation context
				// Need to pass the campaign creator as the user for unsubscribe template loading
				const impersonationContextWithUser = impersonationContext
					? {
							...impersonationContext,
							user: campaign.creator,
						}
					: undefined;

				const unsubscribeTemplateViewModel = new Api.UnsubscribeTemplateViewModel(userSession).impersonate(
					impersonationContextWithUser
				);
				await unsubscribeTemplateViewModel.loadUserTemplate();
				if (unsubscribeTemplateViewModel.userTemplate) {
					setUnsubscribeTemplate(unsubscribeTemplateViewModel.userTemplate);
				} else {
					await unsubscribeTemplateViewModel.loadAccountTemplate();
					setUnsubscribeTemplate(unsubscribeTemplateViewModel.accountTemplate || null);
				}

				setUnsubscribeTemplateLoaded(true);
			} catch {
				setUnsubscribeTemplateLoaded(true);
			}
		};

		if (userSession && campaign) {
			loadUnsubscribeTemplate();
		}
	}, [userSession, impersonationContext, campaign]);

	if (campaignQuery.isLoading) {
		return (
			<div className={css(styleSheet.loadingContainer)}>
				<LoadingSpinner type='large' className='absolute-center' />
			</div>
		);
	}

	if (campaignQuery.error) {
		return (
			<div className={css(styleSheet.errorContainer)}>
				<h2>Failed to load campaign</h2>
				<p>Error: {campaignQuery.error.message || 'Unknown error occurred'}</p>
			</div>
		);
	}

	if (!campaign) {
		return (
			<div className={css(styleSheet.errorContainer)}>
				<h2>Campaign not found</h2>
				<p>The requested campaign could not be loaded.</p>
			</div>
		);
	}

	const renderInfoSection = (title: string, children: React.ReactNode) => (
		<div className={css(styleSheet.infoSection)}>
			<h3 className={css(styleSheet.sectionTitle)}>{title}</h3>
			<div className={css(styleSheet.sectionContent)}>{children}</div>
		</div>
	);

	const renderInfoRow = (label: string, value: React.ReactNode, fallback = 'None') => (
		<div className={css(styleSheet.infoRow)}>
			<span className={css(styleSheet.infoLabel)}>{label}:</span>
			<span className={css(styleSheet.infoValue)}>{value || fallback}</span>
		</div>
	);

	const renderEmailPreview = () => {
		if (campaignReportContentQuery.isLoading) {
			return <LoadingSpinner type='large' />;
		}

		if (campaignReportContentQuery.error) {
			return (
				<div className={css(styleSheet.errorMessage)}>
					Failed to load email content: {campaignReportContentQuery.error.message || 'Unknown error'}
				</div>
			);
		}

		if (!campaignReportContentQuery.data?.document) {
			return <div className={css(styleSheet.previewPlaceholder)}>No email content available for preview.</div>;
		}

		return (
			<EmailPreviewContent
				campaignContent={campaignReportContentQuery.data.document}
				attachments={campaign?.attachments || []}
				signatureTemplate={signatureTemplate}
				unsubscribeTemplate={unsubscribeTemplate}
				templatesLoaded={!signatureTemplateLoading && unsubscribeTemplateLoaded}
				campaign={campaign}
			/>
		);
	};

	return (
		<div className={css(styleSheet.container)}>
			<div className={css(styleSheet.headerContainer)}>
				<h1 className={css(styleSheet.mainTitle)}>Campaign Debug: {campaign.name || 'Untitled Campaign'}</h1>
				<p className={css(styleSheet.subtitle)}>ID: {campaign.id}</p>
			</div>

			<div className={css(styleSheet.contentContainer)}>
				{/* Email Preview Section */}
				{renderInfoSection('Email Preview', renderEmailPreview())}

				{/* Campaign Metadata Section */}
				{renderInfoSection(
					'Campaign Information',
					<>
						{renderInfoRow('Campaign ID', campaign.id)}
						{renderInfoRow('Name', campaign.name)}
						{renderInfoRow('Subject', campaign.subject)}
						{renderInfoRow('Status', campaign.status)}
						{renderInfoRow(
							'Created by',
							campaign.creator
								? `${campaign.creator.firstName || ''} ${campaign.creator.lastName || ''}`.trim() ||
										campaign.creator.primaryEmail?.value ||
										'Unknown'
								: null
						)}
						{renderInfoRow(
							'Creation date',
							campaign.creationDate ? moment(campaign.creationDate).format('MMMM D, YYYY [at] h:mm A') : null
						)}
						{renderInfoRow(
							'Last modified',
							campaign.lastModifiedDate ? moment(campaign.lastModifiedDate).format('MMMM D, YYYY [at] h:mm A') : null
						)}
						{renderInfoRow(
							'Completed date',
							campaign.completedDate ? moment(campaign.completedDate).format('MMMM D, YYYY [at] h:mm A') : null
						)}
						{renderInfoRow(
							'Cancelled date',
							campaign.cancelledDate ? moment(campaign.cancelledDate).format('MMMM D, YYYY [at] h:mm A') : null
						)}
						{renderInfoRow('Total contacts', campaign.totalContacts)}
						{renderInfoRow('Total emails', campaign.totalEmails)}
					</>
				)}

				{/* Template Information Section */}
				{renderInfoSection(
					'Template Information',
					<>
						{renderInfoRow('Template name', campaign.templateReference?.name)}
						{renderInfoRow('Template ID', campaign.templateReference?.templateId)}
						{renderInfoRow('Signature template ID', campaign.signatureTemplateId || 'None')}
						{renderInfoRow('Template type', campaign.templateReference?.templateType)}
						{renderInfoRow('Template category', campaign.templateReference?.category)}
						{renderInfoRow('Custom template', campaign.templateReference?.isCustomized ? 'Yes' : 'No')}
						{renderInfoRow('System template', campaign.templateReference?.isSystemTemplate ? 'Yes' : 'No')}
						{campaign.templateReference?.aiReference &&
							renderInfoRow('AI Session ID', campaign.templateReference.aiReference.sessionId)}
						{campaign.templateReference?.associatedTemplates?.length > 0 &&
							renderInfoRow(
								'Associated templates',
								campaign.templateReference.associatedTemplates.map(t => `${t.templateType} (${t.id})`).join(', ')
							)}
					</>
				)}

				{/* Send On Behalf (Actor) Information */}
				{renderInfoSection(
					'Send Information',
					<>
						{renderInfoRow('Actor name', campaign.actor?.name)}
						{renderInfoRow('Actor account ID', campaign.actor?.accountId)}
						{renderInfoRow('Levitate support user', campaign.actor?.isLevitateSupport ? 'Yes' : 'No')}
						{renderInfoRow('Mailer type', campaign.mailerType)}
						{renderInfoRow(
							'Signature',
							signatureTemplateLoading
								? 'Loading signature...'
								: signatureTemplateError
									? `Error: ${signatureTemplateError.message || 'Unknown error'}`
									: signatureTemplate
										? `${signatureTemplate.name} (ID: ${signatureTemplate.id})`
										: hasValidSignatureTemplateId
											? `No template found for ID: ${campaign.signatureTemplateId}`
											: 'No signature template set'
						)}
						{renderInfoRow(
							'Unsubscribe signature',
							unsubscribeTemplateLoaded
								? unsubscribeTemplate
									? `${unsubscribeTemplate.name} ${(campaign?.totalContacts || 0) <= UNSUBSCRIBE_VISIBILITY_THRESHOLD ? `(hidden - ≤${UNSUBSCRIBE_VISIBILITY_THRESHOLD} recipients)` : `(shown - >${UNSUBSCRIBE_VISIBILITY_THRESHOLD} recipients)`}`
									: 'No unsubscribe template found'
								: 'Loading unsubscribe template...'
						)}
					</>
				)}

				{/* Schedule Information */}
				{renderInfoSection(
					'Schedule Information',
					<>
						{renderInfoRow(
							'Schedule start date',
							campaign.schedule?.startDate
								? moment(campaign.schedule.startDate).format('MMMM D, YYYY [at] h:mm A')
								: null
						)}
						{renderInfoRow(
							'Schedule end date',
							campaign.schedule?.endDate ? moment(campaign.schedule.endDate).format('MMMM D, YYYY [at] h:mm A') : null
						)}
						{renderInfoRow(
							'Schedule expiration',
							campaign.schedule?.expirationDate
								? moment(campaign.schedule.expirationDate).format('MMMM D, YYYY [at] h:mm A')
								: null
						)}
						{renderInfoRow('Send to household', campaign.sendToHousehold ? 'Yes' : 'No')}
						{renderInfoRow('Schedule criteria', campaign.schedule?.criteria)}
					</>
				)}

				{/* Approval Information */}
				{campaign.approvalRequests && campaign.approvalRequests.length > 0
					? renderInfoSection(
							'Approval Information',
							<>
								{campaign.approvalRequests.map((approval, index) => (
									<div key={index} className={css(styleSheet.approvalItem)}>
										{renderInfoRow(`Approval Request #${index + 1} ID`, approval.id)}
										{renderInfoRow('Approval Status', approval.response?.status)}
										{renderInfoRow(
											'Approver',
											approval.approver
												? `${approval.approver.firstName || ''} ${approval.approver.lastName || ''}`.trim() || 'Unknown'
												: null
										)}
										{renderInfoRow(
											'Request Date',
											approval.creationDate ? moment(approval.creationDate).format('MMMM D, YYYY [at] h:mm A') : null
										)}
										{renderInfoRow(
											'Response Date',
											approval.response?.creationDate
												? moment(approval.response.creationDate).format('MMMM D, YYYY [at] h:mm A')
												: null
										)}
									</div>
								))}
							</>
						)
					: renderInfoSection('Approval Information', <div>No approval required</div>)}

				{/* Statistics Section */}
				{renderInfoSection(
					'Statistics',
					<>
						{renderInfoRow('Opens', campaign.openCount)}
						{renderInfoRow('Distinct clicks', campaign.distinctClickCount)}
						{renderInfoRow('Total clicks', campaign.clickCount)}
						{renderInfoRow('Replies', campaign.replyCount)}
						{renderInfoRow('Failures', campaign.failedCount)}
						{renderInfoRow('Bounces', campaign.bouncedCount)}
						{renderInfoRow('Unsubscribes', campaign.unsubscribedCount)}
						{renderInfoRow('Successfully sent', campaign.sentSuccessfullyCount)}
						{renderInfoRow('No email address', campaign.noEmailAddressCount)}
					</>
				)}

				{/* Email Provider and Rate Limiting Section */}
				{renderInfoSection(
					'Email Provider & Rate Limits',
					<EmailProviderAndLimitsInfo
						mailerType={campaign.mailerType}
						account={accountQuery.data}
						accountLoading={accountQuery.isLoading}
						accountQuery={accountQuery}
					/>
				)}

				{/* Attachments Section */}
				{campaign.attachments && campaign.attachments.length > 0
					? renderInfoSection(
							'Attachments',
							<div className={css(styleSheet.attachmentsList)}>
								{campaign.attachments.map((attachment, index) => (
									<div key={index} className={css(styleSheet.attachmentItem)}>
										<span className={css(styleSheet.attachmentName)}>{attachment.fileName}</span>
										{attachment.fileSize && (
											<span className={css(styleSheet.attachmentSize)}>({attachment.fileSize} bytes)</span>
										)}
									</div>
								))}
							</div>
						)
					: renderInfoSection('Attachments', <div>No attachments</div>)}

				{/* Filter/Audience Section */}
				{renderInfoSection(
					'Audience Information',
					<>
						{renderInfoRow('Has contact exclusions', campaign.hasContactExclusions ? 'Yes' : 'No')}
						{renderInfoRow('Send to household', campaign.sendToHousehold ? 'Yes' : 'No')}
						{campaign.sampleContacts && campaign.sampleContacts.length > 0 ? (
							<div className={css(styleSheet.sampleContactsContainer)}>
								<div className={css(styleSheet.infoLabel)}>Sample contacts:</div>
								<div className={css(styleSheet.sampleContactsList)}>
									{campaign.sampleContacts.map((contact, index) => (
										<div key={index} className={css(styleSheet.sampleContact)}>
											{`${contact.firstName || ''} ${contact.lastName || ''}`.trim() ||
												contact.primaryEmail?.value ||
												contact.handle ||
												'Unknown Contact'}
										</div>
									))}
								</div>
							</div>
						) : (
							renderInfoRow('Sample contacts', null)
						)}
						{campaign.filterRequest && (
							<div className={css(styleSheet.filterContainer)}>
								<div className={css(styleSheet.infoLabel)}>Filter criteria:</div>
								<FilterCriteriaRenderer filter={campaign.filterRequest} />
								<FilterRawJsonAccordion filter={campaign.filterRequest} />
							</div>
						)}
					</>
				)}
			</div>
		</div>
	);
}

interface EmailPreviewContentProps {
	campaignContent: string;
	attachments: any[];
	signatureTemplate: Api.ITemplate | null;
	unsubscribeTemplate: Api.ITemplate | null;
	templatesLoaded: boolean;
	campaign: Api.ICampaign | undefined;
}

const EmailPreviewContent: React.FC<EmailPreviewContentProps> = ({
	campaignContent,
	attachments,
	signatureTemplate,
	unsubscribeTemplate,
	templatesLoaded,
	campaign,
}) => {
	// Check if content already includes signature/unsubscribe content
	const hasSignatureContent = campaignContent.includes('signature') || campaignContent.includes('unsubscribe');

	let enhancedContent = campaignContent;

	// If no signature content is found in the campaign content, append the actual templates
	if (!hasSignatureContent && templatesLoaded) {
		const signatureHtml = signatureTemplate?.content?.document
			? `<div style="margin-top: 20px; border-top: 1px solid #eee; padding-top: 10px;">${signatureTemplate.content.document}</div>`
			: '<div style="margin-top: 20px; padding: 10px; background: #f9f9f9; border-left: 3px solid #ccc; color: #666; font-style: italic;">No signature template found</div>';

		// Unsubscribe signature only shows if: unsubscribeTemplate exists AND totalContacts > UNSUBSCRIBE_VISIBILITY_THRESHOLD AND not a key date campaign
		// Note: We don't have keyDateKind info in campaign data, so we assume non-key-date for now
		const shouldShowUnsubscribe =
			unsubscribeTemplate && (campaign?.totalContacts || 0) > UNSUBSCRIBE_VISIBILITY_THRESHOLD;

		let unsubscribeHtml = '';
		if (shouldShowUnsubscribe) {
			unsubscribeHtml = unsubscribeTemplate.content?.document
				? `<div style="margin-top: 10px; border-top: 1px solid #eee; padding-top: 10px; font-size: 12px; color: #666;">${unsubscribeTemplate.content.document}</div>`
				: '<div style="margin-top: 10px; padding: 10px; background: #f9f9f9; border-left: 3px solid #ccc; color: #666; font-style: italic; font-size: 12px;">No unsubscribe template found</div>';
		} else if (unsubscribeTemplate && (campaign?.totalContacts || 0) <= UNSUBSCRIBE_VISIBILITY_THRESHOLD) {
			unsubscribeHtml = `<div style="margin-top: 10px; padding: 10px; background: #fff3cd; border-left: 3px solid #ffc107; color: #856404; font-style: italic; font-size: 12px;">Unsubscribe signature hidden (≤${UNSUBSCRIBE_VISIBILITY_THRESHOLD} recipients)</div>`;
		}

		enhancedContent = `${campaignContent}${signatureHtml}${unsubscribeHtml}`;
	} else if (!hasSignatureContent && !templatesLoaded) {
		// Still loading templates
		enhancedContent = `${campaignContent}<br/><div style="margin-top: 20px; padding: 10px; background: #f0f0f0; color: #666; font-style: italic;">Loading signature and unsubscribe templates...</div>`;
	}

	return (
		<div className={css(styleSheet.emailPreviewContainer)}>
			<div className={css(styleSheet.emailPreviewContent)} dangerouslySetInnerHTML={{ __html: enhancedContent }} />
			{attachments?.length > 0 && (
				<div className={css(styleSheet.attachmentsList)}>
					<div className={css(styleSheet.infoLabel)}>Attachments:</div>
					{attachments.map((attachment, index) => (
						<div key={index} className={css(styleSheet.attachmentItem)}>
							<span className={css(styleSheet.attachmentName)}>{attachment.fileName}</span>
							{attachment.fileSize && (
								<span className={css(styleSheet.attachmentSize)}>({attachment.fileSize} bytes)</span>
							)}
						</div>
					))}
				</div>
			)}
		</div>
	);
};

interface FilterCriteriaRendererProps {
	filter: Api.ContactFilter;
}

const FilterCriteriaRenderer: React.FC<FilterCriteriaRendererProps> = ({ filter }) => {
	const getPropertyName = (property: Api.ContactFilterCriteriaProperty): string => {
		const propertyNames: Partial<Record<Api.ContactFilterCriteriaProperty, string>> = {
			[Api.ContactFilterCriteriaProperty.All]: 'All Contacts',
			[Api.ContactFilterCriteriaProperty.Connections]: 'Connections',
			[Api.ContactFilterCriteriaProperty.CreatedBy]: 'Created By',
			[Api.ContactFilterCriteriaProperty.OwnedBy]: 'Owned By',
			[Api.ContactFilterCriteriaProperty.PrivateContacts]: 'Private Contacts',
			[Api.ContactFilterCriteriaProperty.KeepInTouch]: 'Keep in Touch',
			[Api.ContactFilterCriteriaProperty.TagAlert]: 'Tag Alert',
			[Api.ContactFilterCriteriaProperty.Name]: 'Name',
			[Api.ContactFilterCriteriaProperty.Company]: 'Company',
			[Api.ContactFilterCriteriaProperty.Tag]: 'Tag',
			[Api.ContactFilterCriteriaProperty.Email]: 'Email',
			[Api.ContactFilterCriteriaProperty.UpcomingKeyDates]: 'Upcoming Key Dates',
			[Api.ContactFilterCriteriaProperty.UpcomingKeyDatesNotScheduled]: 'Upcoming Key Dates (Not Scheduled)',
			[Api.ContactFilterCriteriaProperty.WithEmailAddress]: 'Has Email Address',
			[Api.ContactFilterCriteriaProperty.WithoutEmailAddresses]: 'No Email Address',
			[Api.ContactFilterCriteriaProperty.WithoutTags]: 'No Tags',
			[Api.ContactFilterCriteriaProperty.InProgressAutomations]: 'In Progress Automations',
			[Api.ContactFilterCriteriaProperty.OnHoldAutomations]: 'On Hold Automations',
			[Api.ContactFilterCriteriaProperty.PreviousAutomations]: 'Previous Automations',
			[Api.ContactFilterCriteriaProperty.Policy]: 'Policy',
			[Api.ContactFilterCriteriaProperty.KeyDate]: 'Key Date',
			[Api.ContactFilterCriteriaProperty.KeyDateKind]: 'Key Date Kind',
			[Api.ContactFilterCriteriaProperty.KeyDateYear]: 'Key Date Year',
			[Api.ContactFilterCriteriaProperty.KeyDateSuppressList]: 'Key Date Suppress List',
			[Api.ContactFilterCriteriaProperty.KeyDateSuppressDate]: 'Key Date Suppress Date',
			[Api.ContactFilterCriteriaProperty.Archived]: 'Archived',
			[Api.ContactFilterCriteriaProperty.CapableOf]: 'Capable Of',
			[Api.ContactFilterCriteriaProperty.PhoneNumber]: 'Phone Number',
			[Api.ContactFilterCriteriaProperty.Source]: 'Source',
			[Api.ContactFilterCriteriaProperty.WithMailingAddress]: 'Has Mailing Address',
			[Api.ContactFilterCriteriaProperty.ConnectionTypeOrOwner]: 'Connection Type or Owner',
			[Api.ContactFilterCriteriaProperty.WithPhoneNumber]: 'Has Phone Number',
			[Api.ContactFilterCriteriaProperty.WithoutPhoneNumber]: 'No Phone Number',
			[Api.ContactFilterCriteriaProperty.HasDuplicates]: 'Has Duplicates',
			[Api.ContactFilterCriteriaProperty.ConnectionType]: 'Connection Type',
			[Api.ContactFilterCriteriaProperty.CreationDate]: 'Creation Date',
			[Api.ContactFilterCriteriaProperty.LastCommunicationDate]: 'Last Communication',
			[Api.ContactFilterCriteriaProperty.City]: 'City',
			[Api.ContactFilterCriteriaProperty.StateProvince]: 'State/Province',
			[Api.ContactFilterCriteriaProperty.PostalCode]: 'Postal Code',
			[Api.ContactFilterCriteriaProperty.JobTitle]: 'Job Title',
		};
		return propertyNames[property as keyof typeof propertyNames] || `Unknown Property (${property})`;
	};

	const renderCriteria = (criteria: Api.IContactFilterCriteria, depth = 0): React.ReactNode => {
		const indentStyle = { marginLeft: `${depth * 20}px` };

		// If this has nested criteria, render as a group
		if (criteria.criteria && criteria.criteria.length > 0) {
			const groupOperator = criteria.op === 'Or' ? 'OR' : 'AND';
			return (
				<div key={JSON.stringify(criteria)} style={indentStyle} className={css(styleSheet.filterGroup)}>
					<div className={css(styleSheet.filterGroupOperator)}>{depth > 0 && `${groupOperator} (`}</div>
					<div className={css(styleSheet.filterGroupContent)}>
						{criteria.criteria.map((subCriteria, index) => (
							<div key={index}>
								{index > 0 && <div className={css(styleSheet.filterOperator)}>{groupOperator}</div>}
								{renderCriteria(subCriteria, depth + 1)}
							</div>
						))}
					</div>
					{depth > 0 && <div className={css(styleSheet.filterGroupOperator)}>)</div>}
				</div>
			);
		}

		// Render individual criteria
		const propertyName = criteria.property !== undefined ? getPropertyName(criteria.property) : 'Unknown';
		const value =
			criteria.value ||
			(criteria.values && Object.keys(criteria.values).length > 0
				? Object.keys(criteria.values)
						.map(x => criteria.values[x])
						.join(', ')
				: '');
		const operator = criteria.op || '';

		let operatorText = '';
		switch (operator) {
			case 'Not':
				operatorText = value ? 'is not' : 'does not exist';
				break;
			case 'Gte':
				operatorText = 'is greater than or equal to';
				break;
			case 'Lte':
				operatorText = 'is less than or equal to';
				break;
			default:
				operatorText = value ? 'is' : 'exists';
		}

		return (
			<div key={JSON.stringify(criteria)} style={indentStyle} className={css(styleSheet.filterCriteria)}>
				<span className={css(styleSheet.filterProperty)}>{propertyName}</span>
				{operatorText && <span className={css(styleSheet.filterOperatorText)}> {operatorText} </span>}
				{value && <span className={css(styleSheet.filterValue)}>&quot;{value}&quot;</span>}
			</div>
		);
	};

	if (!filter.criteria || filter.criteria.length === 0) {
		return <div className={css(styleSheet.filterEmpty)}>No filter criteria specified</div>;
	}

	return (
		<div className={css(styleSheet.filterRenderer)}>
			{filter.criteria.map((criteria, index) => (
				<div key={index}>
					{index > 0 && <div className={css(styleSheet.filterOperator)}>{filter.op === 'Or' ? 'OR' : 'AND'}</div>}
					{renderCriteria(criteria)}
				</div>
			))}
		</div>
	);
};

interface FilterRawJsonAccordionProps {
	filter: Api.ContactFilter;
}

const FilterRawJsonAccordion: React.FC<FilterRawJsonAccordionProps> = ({ filter }) => {
	const [isExpanded, setIsExpanded] = React.useState(false);

	const toggleExpanded = () => {
		setIsExpanded(!isExpanded);
	};

	return (
		<div className={css(styleSheet.filterAccordion)}>
			<button className={css(styleSheet.filterAccordionHeader)} onClick={toggleExpanded} type='button'>
				<span>View Raw JSON</span>
				<span className={css(styleSheet.filterAccordionIcon)}>{isExpanded ? '▼' : '▶'}</span>
			</button>
			{isExpanded && (
				<div className={css(styleSheet.filterAccordionContent)}>
					<pre className={css(styleSheet.filterJson)}>{JSON.stringify(filter, null, 2)}</pre>
				</div>
			)}
		</div>
	);
};

interface EmailProviderAndLimitsInfoProps {
	mailerType?: EmailMailerType;
	account?: Api.IAccount;
	accountLoading: boolean;
	accountQuery: any;
}

const EmailProviderAndLimitsInfo: React.FC<EmailProviderAndLimitsInfoProps> = ({
	mailerType,
	account,
	accountLoading,
	accountQuery,
}) => {
	const getMailerTypeName = (type?: EmailMailerType): string => {
		switch (type) {
			case EmailMailerType.ClientEmailProvider:
				return 'Client Email Provider (Custom SMTP)';
			case EmailMailerType.Levitate:
				return 'Levitate Email Service';
			case EmailMailerType.SendGrid:
				return 'SendGrid';
			case EmailMailerType.LevitateHosted:
				return 'Levitate Hosted';
			case EmailMailerType.LevitateHostedHtml:
				return 'Levitate Hosted (HTML)';
			default:
				return type || 'Unknown';
		}
	};

	const renderRateLimits = (limits?: Api.IEmailSendLimit[]) => {
		if (!limits || limits.length === 0) {
			return <div className={css(styleSheet.infoValue)}>No rate limits configured</div>;
		}

		return (
			<div className={css(styleSheet.rateLimitsList)}>
				{limits.map((limit, index) => (
					<div key={index} className={css(styleSheet.rateLimitItem)}>
						<span className={css(styleSheet.rateLimitCount)}>{limit.count}</span>
						<span className={css(styleSheet.rateLimitInterval)}>
							emails per {limit.intervalInMinutes} minute{limit.intervalInMinutes !== 1 ? 's' : ''}
						</span>
					</div>
				))}
			</div>
		);
	};

	const renderProviderConfig = () => {
		const configs = account?.emailProviderConfigurations;
		const config = configs && configs.length > 0 ? configs[0] : null;
		if (!config) {
			return <div className={css(styleSheet.infoValue)}>No email provider configuration found</div>;
		}

		return (
			<>
				<div className={css(styleSheet.providerConfigRow)}>
					<span className={css(styleSheet.infoLabel)}>Max message size:</span>
					<span className={css(styleSheet.infoValue)}>
						{config.maxMessageSizeInBytes
							? `${(config.maxMessageSizeInBytes / (1024 * 1024)).toFixed(1)} MB`
							: 'Not specified'}
					</span>
				</div>
				{config.secondsBetweenSends && (
					<div className={css(styleSheet.providerConfigRow)}>
						<span className={css(styleSheet.infoLabel)}>Seconds between sends:</span>
						<span className={css(styleSheet.infoValue)}>{config.secondsBetweenSends}s</span>
					</div>
				)}
				{config.sendFrom?.domain && (
					<div className={css(styleSheet.providerConfigRow)}>
						<span className={css(styleSheet.infoLabel)}>Send from domain:</span>
						<span className={css(styleSheet.infoValue)}>{config.sendFrom.domain}</span>
					</div>
				)}
				<div className={css(styleSheet.providerConfigRow)}>
					<span className={css(styleSheet.infoLabel)}>Rate limits:</span>
					{renderRateLimits(config.emailSendLimits)}
				</div>
			</>
		);
	};

	if (accountLoading) {
		return <LoadingSpinner type='small' />;
	}

	if (accountQuery.error) {
		return (
			<div className={css(styleSheet.errorMessage)}>
				Failed to load account information: {accountQuery.error.message || 'Unknown error'}
			</div>
		);
	}

	return (
		<>
			<div className={css(styleSheet.providerConfigRow)}>
				<span className={css(styleSheet.infoLabel)}>Email provider:</span>
				<span className={css(styleSheet.infoValue)}>{getMailerTypeName(mailerType)}</span>
			</div>
			{renderProviderConfig()}
		</>
	);
};
